import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { apiDelete, apiGet, apiPost, apiPut } from "../../shared/apiClient";
import { useAuth } from "../auth/AuthContext";
import { useToast } from "../../shared/ToastContext";

const empty = {
  fullName: "",
  mobileNo: "",
  email: "",
  designation: "",
  department: "",
  userName: "",
  password: "",
  customerId: "",
  isBlocked: false
};
const inputClass =
  "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 outline-none focus:border-brand-600 focus:ring-2 focus:ring-brand-600/20";

export function EmployeesPage() {
  const { customerId: routeCustomerId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const toast = useToast();
  const isAdmin = String(user?.role || "").toLowerCase() === "admin";
  const canManage = isAdmin || String(user?.role || "").toLowerCase() === "customer";
  const [customers, setCustomers] = useState([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState(routeCustomerId || "");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const lockedCustomer = useMemo(
    () => customers.find((c) => String(c.id) === String(routeCustomerId || selectedCustomerId)),
    [customers, routeCustomerId, selectedCustomerId]
  );

  const load = useCallback(async () => {
    if (!canManage) return;
    setLoading(true);
    const q = selectedCustomerId ? `?customerId=${encodeURIComponent(selectedCustomerId)}` : "";
    const res = await apiGet(`/api/Employees${q}`);
    if (res.ok) setRows(Array.isArray(res.data) ? res.data : []);
    else toast.push(String(res.error || "Failed to load employees"), "error");
    setLoading(false);
  }, [canManage, selectedCustomerId, toast]);

  useEffect(() => {
    const loadCustomers = async () => {
      if (!isAdmin) return;
      const res = await apiGet("/api/Customers");
      if (res.ok) setCustomers(Array.isArray(res.data) ? res.data : []);
    };
    void loadCustomers();
  }, [isAdmin]);

  useEffect(() => {
    if (routeCustomerId) setSelectedCustomerId(routeCustomerId);
  }, [routeCustomerId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (!canManage) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center rounded-2xl border border-slate-200 bg-white p-8 text-slate-600 shadow-sm">
        You do not have access to this section.
      </div>
    );
  }

  const openCreate = () => {
    setEditingId(null);
    setForm({ ...empty, customerId: selectedCustomerId || user?.customerId || "" });
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setEditingId(row.id);
    setForm({
      userName: row.userName || "",
      fullName: row.fullName || "",
      mobileNo: row.mobileNo || "",
      email: row.email || "",
      designation: row.designation || "",
      department: row.department || "",
      password: "",
      customerId: row.customerId || selectedCustomerId || "",
      isBlocked: Boolean(row.isBlocked)
    });
    setModalOpen(true);
  };

  const onSave = async (e) => {
    e.preventDefault();
    if (!form.userName.trim()) {
      toast.push("Username is required", "error");
      return;
    }
    if (!editingId && !form.password.trim()) {
      toast.push("Password is required", "error");
      return;
    }
    if (isAdmin && !form.customerId) {
      toast.push("Customer is required", "error");
      return;
    }

    setSaving(true);
    const body = {
      id: editingId || 0,
      userName: form.userName,
      password: form.password || null,
      fullName: form.fullName || null,
      mobileNo: form.mobileNo || null,
      email: form.email || null,
      designation: form.designation || null,
      department: form.department || null,
      customerId: form.customerId ? Number(form.customerId) : null,
      isBlocked: form.isBlocked
    };
    const res = editingId ? await apiPut(`/api/Employees/${editingId}`, body) : await apiPost("/api/Employees", body);
    setSaving(false);
    if (!res.ok) {
      toast.push(String(res.error), "error");
      return;
    }
    toast.push(editingId ? "Employee updated" : "Employee created", "success");
    setModalOpen(false);
    await load();
  };

  const runDelete = async () => {
    if (!deleteTarget) return;
    const res = await apiDelete(`/api/Employees/${deleteTarget.id}`);
    if (!res.ok) {
      toast.push(String(res.error), "error");
      return;
    }
    toast.push("Employee deleted", "success");
    setDeleteTarget(null);
    await load();
  };

  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold text-slate-900 sm:text-2xl">Employees</h1>
            <p className="mt-1 text-sm text-slate-500">
              Manage employees{lockedCustomer ? ` for ${lockedCustomer.name || lockedCustomer.code}` : " under customers"}.
            </p>
          </div>
          <button
            type="button"
            onClick={openCreate}
            className="rounded-lg bg-brand-800 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-brand-700"
          >
            Add employee
          </button>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-2">
          {isAdmin && !routeCustomerId && (
            <select
              className={inputClass}
              value={selectedCustomerId}
              onChange={(e) => setSelectedCustomerId(e.target.value)}
            >
              <option value="">All customers</option>
              {customers.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name || c.code || `Customer ${c.id}`}
                </option>
              ))}
            </select>
          )}
          {routeCustomerId && (
            <button
              type="button"
              onClick={() => navigate("/customers")}
              className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              Back to customers
            </button>
          )}
        </div>
      </div>

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        {loading ? (
          <div className="flex items-center justify-center py-16 text-sm text-slate-500">Loading...</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1100px] text-left text-sm">
              <thead>
                <tr className="bg-slate-50">
                  <th className="px-4 py-3 text-xs font-semibold text-slate-500">Employee</th>
                  <th className="px-4 py-3 text-xs font-semibold text-slate-500">Contact</th>
                  <th className="px-4 py-3 text-xs font-semibold text-slate-500">Designation</th>
                  <th className="px-4 py-3 text-xs font-semibold text-slate-500">Department</th>
                  <th className="px-4 py-3 text-xs font-semibold text-slate-500">Customer</th>
                  <th className="px-4 py-3 text-xs font-semibold text-slate-500">Status</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((row) => (
                  <tr key={row.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3">
                      <p className="font-medium text-slate-900">{row.fullName || row.userName || "-"}</p>
                      <p className="text-xs text-slate-500">{row.userName || "-"}</p>
                    </td>
                    <td className="px-4 py-3 text-slate-600">
                      <p>{row.mobileNo || "-"}</p>
                      <p className="text-xs text-slate-500">{row.email || ""}</p>
                    </td>
                    <td className="px-4 py-3 text-slate-600">{row.designation || "-"}</td>
                    <td className="px-4 py-3 text-slate-600">{row.department || "-"}</td>
                    <td className="px-4 py-3 text-slate-600">{row.customerName || "-"}</td>
                    <td className="px-4 py-3">
                      <span className={`rounded-full px-2 py-1 text-xs font-semibold ${row.isBlocked ? "bg-red-50 text-red-700" : "bg-emerald-50 text-emerald-700"}`}>
                        {row.isBlocked ? "Blocked" : "Active"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button type="button" onClick={() => openEdit(row)} className="mr-2 rounded-lg px-3 py-1.5 text-sm font-medium text-brand-800 hover:bg-emerald-50">
                        Edit
                      </button>
                      <button type="button" onClick={() => setDeleteTarget(row)} className="rounded-lg px-3 py-1.5 text-sm font-medium text-red-600 hover:bg-red-50">
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-12 text-center text-slate-500">
                      No employees found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="presentation">
          <button type="button" className="absolute inset-0 bg-slate-900/50" onClick={() => setModalOpen(false)} aria-label="Close" />
          <form onSubmit={onSave} className="relative z-10 w-full max-w-2xl rounded-2xl bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-lg font-semibold text-slate-900">{editingId ? "Edit employee" : "Add employee"}</h2>
            <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
              {isAdmin && (
                <div className="sm:col-span-2">
                  <label className="mb-1.5 block text-xs font-medium text-slate-600">Customer *</label>
                  <select className={inputClass} value={form.customerId} onChange={(e) => setForm((prev) => ({ ...prev, customerId: e.target.value }))}>
                    <option value="">Select customer</option>
                    {customers.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name || c.code || `Customer ${c.id}`}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div>
                <label className="mb-1.5 block text-xs font-medium text-slate-600">Full name</label>
                <input className={inputClass} value={form.fullName} onChange={(e) => setForm((prev) => ({ ...prev, fullName: e.target.value }))} />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-medium text-slate-600">Mobile no</label>
                <input className={inputClass} value={form.mobileNo} onChange={(e) => setForm((prev) => ({ ...prev, mobileNo: e.target.value }))} />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-medium text-slate-600">Email</label>
                <input type="email" className={inputClass} value={form.email} onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))} />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-medium text-slate-600">Designation</label>
                <input className={inputClass} value={form.designation} onChange={(e) => setForm((prev) => ({ ...prev, designation: e.target.value }))} />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-medium text-slate-600">Department</label>
                <input className={inputClass} value={form.department} onChange={(e) => setForm((prev) => ({ ...prev, department: e.target.value }))} />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-medium text-slate-600">Username *</label>
                <input className={inputClass} value={form.userName} onChange={(e) => setForm((prev) => ({ ...prev, userName: e.target.value }))} />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-medium text-slate-600">
                  Password{editingId ? " (leave blank to keep current)" : " *"}
                </label>
                <input type="password" className={inputClass} value={form.password} onChange={(e) => setForm((prev) => ({ ...prev, password: e.target.value }))} />
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input type="checkbox" checked={form.isBlocked} onChange={(e) => setForm((prev) => ({ ...prev, isBlocked: e.target.checked }))} />
                Block employee login
              </label>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button type="button" onClick={() => setModalOpen(false)} className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">
                Cancel
              </button>
              <button type="submit" disabled={saving} className="rounded-lg bg-brand-800 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-60">
                {saving ? "Saving..." : "Save"}
              </button>
            </div>
          </form>
        </div>
      )}

      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="presentation">
          <button type="button" className="absolute inset-0 bg-slate-900/50" onClick={() => setDeleteTarget(null)} aria-label="Close" />
          <div className="relative z-10 w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
            <h2 className="text-lg font-semibold text-slate-900">Delete employee?</h2>
            <p className="mt-2 text-sm text-slate-600">This will remove {deleteTarget.userName || "this employee"}.</p>
            <div className="mt-6 flex justify-end gap-2">
              <button type="button" onClick={() => setDeleteTarget(null)} className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">
                Cancel
              </button>
              <button type="button" onClick={() => void runDelete()} className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-500">
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
